import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, FileText } from 'lucide-react';
import type { InfrastructureFinishField } from '../infrastructureAutoFill';

interface FinishInfrastructureDialogProps {
  templateName: string;
  initialName: string;
  fields?: InfrastructureFinishField[];
  isSubmitting?: boolean;
  onConfirm: (payload: { name: string; attributes: Record<string, unknown> }) => void;
  onCancel: () => void;
}

function formatFieldValue(value: unknown, unit?: string) {
  if (value === null || value === undefined || value === '') return 'Not set';
  if (typeof value === 'number') {
    const formatted = Number.isInteger(value) ? value.toString() : value.toFixed(2);
    return unit ? `${formatted} ${unit}` : formatted;
  }
  return unit ? `${String(value)} ${unit}` : String(value);
}

function normalizeEditableValue(rawValue: string, type?: string) {
  if (rawValue === '') return null;
  if (type === 'number' || type === 'integer' || type === 'float') {
    const numericValue = Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : rawValue;
  }
  return rawValue;
}

export default function FinishInfrastructureDialog({
  templateName,
  initialName,
  fields = [],
  isSubmitting,
  onConfirm,
  onCancel,
}: FinishInfrastructureDialogProps) {
  const [name, setName] = useState(initialName);
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({});

  useEffect(() => {
    setName(initialName);
  }, [initialName]);

  useEffect(() => {
    const nextValues: Record<string, unknown> = {};
    fields.forEach((field) => {
      nextValues[field.key] = field.value ?? null;
    });
    setFieldValues(nextValues);
  }, [fields]);

  const editableFields = useMemo(
    () => fields.filter((field) => field.editable),
    [fields]
  );
  const readOnlyFields = useMemo(
    () => fields.filter((field) => !field.editable && field.value !== null && field.value !== undefined && field.value !== ''),
    [fields]
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-40 flex items-center justify-center bg-slate-900/35 px-4 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.96, y: 12, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.96, y: 12, opacity: 0 }}
        className="w-full max-w-lg rounded-2xl border border-sky-200 bg-white p-5 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-slate-900">Finish {templateName}</h3>
            <p className="mt-1 text-sm text-slate-600">
              Confirm the name and review any auto-filled attributes before saving this infrastructure object.
            </p>
          </div>
        </div>

        <label className="mt-4 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
          Name
        </label>
        <input
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && name.trim() && !isSubmitting) {
              onConfirm({ name: name.trim(), attributes: fieldValues });
            }
          }}
          className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none transition-colors focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
          placeholder={templateName}
        />

        {readOnlyFields.length > 0 && (
          <>
            <div className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
              Auto-filled
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {readOnlyFields.map((field) => (
                <div key={field.key} className="rounded-full bg-sky-50 px-3 py-1.5 text-[11px] text-sky-800">
                  <span className="font-semibold">{field.label || field.key}</span>
                  <span className="ml-1">{formatFieldValue(field.value, field.unit)}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {editableFields.length > 0 && (
          <>
            <div className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
              Complete details
            </div>
            <div className="mt-2 space-y-2">
              {editableFields.map((field) => (
                <label key={field.key} className="block">
                  <span className="text-xs font-medium text-slate-600">{field.label || field.key}</span>
                  {field.enum && field.enum.length > 0 ? (
                    <select
                      value={String(fieldValues[field.key] ?? '')}
                      onChange={(event) => setFieldValues((current) => ({ ...current, [field.key]: event.target.value || null }))}
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition-colors focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                    >
                      <option value="">Select...</option>
                      {field.enum.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={String(fieldValues[field.key] ?? '')}
                      onChange={(event) => setFieldValues((current) => ({
                        ...current,
                        [field.key]: normalizeEditableValue(event.target.value, field.type),
                      }))}
                      placeholder={field.unit ? `Value in ${field.unit}` : 'Enter value'}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none transition-colors focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                    />
                  )}
                </label>
              ))}
            </div>
          </>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm({ name: name.trim(), attributes: fieldValues })}
            disabled={!name.trim() || isSubmitting}
            className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Check className="h-4 w-4" />
            {isSubmitting ? 'Saving...' : 'Save infrastructure'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
